import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiChangePassword } from '../services/api';
import toast from 'react-hot-toast';

export default function SettingsPage() {
    const { user } = useAuth();
    const [tab, setTab] = useState('profile');
    const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [pwSaving, setPwSaving] = useState(false);
    const [showPw, setShowPw] = useState({ old: false, new: false, confirm: false });

    const handlePwChange = (e) => setPwForm(f => ({ ...f, [e.target.name]: e.target.value }));

    const handlePwSubmit = async (e) => {
        e.preventDefault();
        if (!pwForm.oldPassword || !pwForm.newPassword || !pwForm.confirmPassword)
            return toast.error('Please fill all fields');
        if (pwForm.newPassword.length < 6)
            return toast.error('New password must be at least 6 characters');
        if (pwForm.newPassword !== pwForm.confirmPassword)
            return toast.error('Passwords do not match');

        setPwSaving(true);
        try {
            await apiChangePassword({ oldPassword: pwForm.oldPassword, newPassword: pwForm.newPassword });
            toast.success('Password changed successfully!');
            setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (e) {
            toast.error(e.response?.data?.message || 'Change failed');
        } finally {
            setPwSaving(false);
        }
    };

    const togglePw = (field) => setShowPw(s => ({ ...s, [field]: !s[field] }));

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Settings</h1>
                    <p className="page-subtitle">Manage your account and preferences</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                <button className={`tab-btn${tab === 'profile' ? ' active' : ''}`} onClick={() => setTab('profile')}>👤 Profile</button>
                <button className={`tab-btn${tab === 'password' ? ' active' : ''}`} onClick={() => setTab('password')}>🔐 Change Password</button>
                <button className={`tab-btn${tab === 'about' ? ' active' : ''}`} onClick={() => setTab('about')}>ℹ️ About</button>
            </div>

            {/* Profile Tab */}
            {tab === 'profile' && (
                <div className="card">
                    <div className="card-header"><span className="card-title">Profile Information</span></div>
                    <div className="card-body">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '32px', flexWrap: 'wrap' }}>
                            <div style={{
                                width: 80, height: 80, borderRadius: '50%',
                                background: 'linear-gradient(135deg, var(--accent), #a78bfa)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '2rem', fontWeight: '700', color: '#fff', flexShrink: 0,
                            }}>
                                {user?.name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                                <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)' }}>{user?.name}</h2>
                                <p style={{ color: 'var(--text-muted)', marginTop: '4px' }}>{user?.email}</p>
                                <span className={`badge badge--${user?.role}`} style={{ marginTop: '8px' }}>{user?.role}</span>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '24px' }}>
                            {[
                                { label: 'Full Name', value: user?.name },
                                { label: 'Email Address', value: user?.email },
                                { label: 'Role', value: user?.role },
                                { label: 'User ID', value: `#${user?.id}` },
                            ].map(({ label, value }) => (
                                <div key={label}>
                                    <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '6px' }}>{label}</div>
                                    <div style={{ fontSize: '0.95rem', color: 'var(--text-primary)' }}>{value}</div>
                                </div>
                            ))}
                        </div>

                        <div style={{
                            marginTop: '28px', padding: '16px 18px', background: 'var(--accent-dim)',
                            borderRadius: 'var(--radius)', border: '1px solid rgba(99,102,241,0.2)',
                        }}>
                            <p style={{ fontSize: '0.85rem', color: 'var(--accent-light)' }}>
                                ℹ️ Profile details are managed by your administrator. Contact your admin to update your name or email.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password Tab */}
            {tab === 'password' && (
                <div className="card" style={{ maxWidth: 480 }}>
                    <div className="card-header"><span className="card-title">Change Password</span></div>
                    <div className="card-body">
                        <form id="change-password-form" onSubmit={handlePwSubmit} noValidate>
                            {[
                                { name: 'oldPassword', label: 'Current Password', field: 'old', id: 'pw-old' },
                                { name: 'newPassword', label: 'New Password', field: 'new', id: 'pw-new' },
                                { name: 'confirmPassword', label: 'Confirm New Password', field: 'confirm', id: 'pw-confirm' },
                            ].map(({ name, label, field, id }) => (
                                <div className="form-group" key={name}>
                                    <label htmlFor={id}>{label}</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            id={id}
                                            name={name}
                                            type={showPw[field] ? 'text' : 'password'}
                                            value={pwForm[name]}
                                            onChange={handlePwChange}
                                            placeholder="••••••••"
                                            style={{ paddingRight: '44px' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePw(field)}
                                            style={{
                                                position: 'absolute', right: '12px', top: '50%',
                                                transform: 'translateY(-50%)', background: 'none',
                                                border: 'none', cursor: 'pointer',
                                                color: 'var(--text-muted)', padding: 0, lineHeight: 1,
                                            }}
                                            aria-label="Toggle visibility"
                                        >
                                            {showPw[field] ? '🙈' : '👁'}
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <div className="form-actions">
                                <button
                                    id="change-password-submit"
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={pwSaving}
                                >
                                    {pwSaving
                                        ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</>
                                        : '🔐 Update Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* About Tab */}
            {tab === 'about' && (
                <div className="card" style={{ maxWidth: 520 }}>
                    <div className="card-header"><span className="card-title">About LeadPro CRM</span></div>
                    <div className="card-body">
                        <div style={{ textAlign: 'center', padding: '20px 0 28px' }}>
                            <div style={{ fontSize: '3.5rem', marginBottom: '12px' }}>🎓</div>
                            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)' }}>LeadPro CRM</h2>
                            <p style={{ color: 'var(--text-muted)', marginTop: '4px' }}>Lead Management System for IT Training Institutes</p>
                            <span style={{ display: 'inline-block', marginTop: '12px', padding: '4px 14px', background: 'var(--accent-dim)', borderRadius: 20, fontSize: '0.8rem', color: 'var(--accent-light)' }}>
                                v1.0.0
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {[
                                { label: 'Frontend', value: 'React.js (CRA)' },
                                { label: 'Backend', value: 'Node.js + Express.js' },
                                { label: 'Database', value: 'MySQL 8' },
                                { label: 'Auth', value: 'JWT (7-day tokens)' },
                                { label: 'Roles', value: 'Admin, Counselor' },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
